import { DropdownListItem, Icon, Tooltip } from '@toggle/design-system';
import clsx from 'clsx';
import React, { CSSProperties, JSX } from 'react';

import type { SeriesType } from '~/types/series.types';

import {
  ChartTypeSelector,
  chartTypeToIconName,
} from '../chart-type-selector/ChartTypeSelector';
import {
  MultiDropdown,
  MultiDropdownItem,
} from '../multi-dropdown/MultiDropdown';
import styles from './LegendMoreMenu.module.scss';

export const LEGEND_MORE_MENU_ID = 'legend-more-menu';

export interface LegendMoreMenuLocales {
  more: string;
  tapToClose: string;
  changeChartColor: string;
  seriesType_line: string;
  seriesType_bars: string;
  seriesType_candlestick: string;
}

export interface LegendMoreMenuProps {
  chartColor: string;
  colorArray: string[];
  onColorSelect: (colorIndex: number) => void;
  changeSeriesType?: (
    chartType: SeriesType,
    { snake, paneIndex }: { snake?: string; paneIndex?: number }
  ) => void;
  renderMovePane?: () => JSX.Element;
  onMenuOpen: (open: boolean) => void;
  hasOHLC?: boolean;
  selectedSeriesType?: SeriesType;
  defaultSnake?: string;
  locales: LegendMoreMenuLocales;
}

export const LegendMoreMenu = ({
  colorArray,
  chartColor,
  onColorSelect,
  changeSeriesType,
  onMenuOpen,
  hasOHLC = false,
  selectedSeriesType = 'line',
  defaultSnake,
  renderMovePane,
  locales,
}: LegendMoreMenuProps) => {
  return (
    <div className={styles.legendMoreMenuRoot}>
      <MultiDropdown
        baseComponent={
          <div>
            <Tooltip
              label={locales.more}
              closeLabel={locales.tapToClose}
              isTouchDevice={false}
              inPortal
            >
              <span className={styles.iconWrapper}>
                <Icon
                  className={styles.menuIcon}
                  iconName="Ellipsis"
                  size={12}
                />
              </span>
            </Tooltip>
          </div>
        }
        onOpen={open => onMenuOpen(open)}
      >
        <MultiDropdown
          baseComponent={
            <DropdownListItem
              id={LEGEND_MORE_MENU_ID}
              label={locales.changeChartColor}
              trailingIcon={<Icon iconName="ChevronLightRight" size={16} />}
              leadingIcon={
                <div
                  className={styles.smallColorIcon}
                  style={{ backgroundColor: chartColor }}
                />
              }
              isActive={false}
              isFocused={false}
            />
          }
        >
          <MultiDropdownItem
            customComponent={
              <div className={styles.colorPicker}>
                {colorArray.map((c, i) => (
                  <div
                    key={i}
                    className={clsx(styles.colorIcon, {
                      [styles.selected]: c === chartColor,
                    })}
                    style={
                      {
                        backgroundColor: c,
                        '--color-icon-color': c,
                      } as CSSProperties
                    }
                    onClick={() => onColorSelect(i)}
                  >
                    {c === chartColor && (
                      <Icon iconName="Checkmark" size={16} />
                    )}
                  </div>
                ))}
              </div>
            }
          />
        </MultiDropdown>
        {changeSeriesType && (
          <MultiDropdown
            baseComponent={
              <DropdownListItem
                id={`${LEGEND_MORE_MENU_ID}-series`}
                label={
                  locales[
                    `seriesType_${selectedSeriesType}` as keyof LegendMoreMenuLocales
                  ]
                }
                trailingIcon={<Icon iconName="ChevronLightRight" size={16} />}
                leadingIcon={
                  <Icon
                    className={styles.leftIcon}
                    data-testid={`${LEGEND_MORE_MENU_ID}-${selectedSeriesType}`}
                    size={12}
                    iconName={chartTypeToIconName[selectedSeriesType]}
                  />
                }
                isActive={false}
                isFocused={false}
              />
            }
          >
            <MultiDropdownItem
              customComponent={
                <ChartTypeSelector
                  selectedSeriesType={selectedSeriesType}
                  onChange={changeSeriesType}
                  disableMenuBar={false}
                  hasOHLC={hasOHLC}
                  defaultSnake={defaultSnake}
                />
              }
            />
          </MultiDropdown>
        )}
        {renderMovePane?.()}
      </MultiDropdown>
    </div>
  );
};
